import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
	Box,
	Button,
	Grid,
	Typography,
	TextField,
	Tooltip,
	InputLabel,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material'
import { debounce } from 'lodash'
import PhoneNumberMask from '../PhoneNumberMask/PhoneNumberMask'
import useInterval from '../../utils/Interval/useInterval'
import CampaignType from '../../pages/CampaignDetails/components/CampaignType'
import { runCampaign } from '../../api/campaign/runCampaign'
import CircularProgress from '@mui/material/CircularProgress'
import { getTimeRemaining } from '../../utils/CalculateTimeRemaining/CalculateTimeRemaining'
import { Info, RefreshCw } from 'react-feather'
import { t } from 'i18next'
import { Trans } from 'react-i18next'
import { useFeatureFlags } from '../../utils/FeatureFlags/useFeatureFlags'
import { useFetchAgent } from '../../api/agents/useFetchAgent'
import {
	inputLabelContainerStyle,
	inputLabelStyle,
	labelTooltipContainerStyle,
	learnMoreStyle,
	linkStyle,
	modalSubtitleStyle,
	refreshStyle,
	testButtonStyle,
} from './CampaignSetup.styles'
import { AdvanceSettings } from '../../pages/CampaignDetails/components/SettingsList/AdvanceSettings'
import SettingsList from '../../pages/CampaignDetails/components/SettingsList/SettingsList'
import useCampaignTypes from '../../store/hooks/agents/useCampaignTypes'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { validatePhoneNumber } from '../../utils/Validators/Validators'
import OpeningHookCustomSelector from './components/OpeningHookCustomSelector/OpeningHookCustomSelector'
import { actionSetCampaignPayload } from '../../store/slices/import/importSlice'
import { useAppDispatch, useAppSelector } from '../../store/utils/hooks'
import { Campaign } from '../../api/campaign/types'
import { actionUpdateCampaignDetails, selectDetailsData } from '../../store/slices/campaign/campaignSlice'
import useCommonVars from '../../utils/FeatureFlags/useCommonVars'

const LOCAL_STORAGE_KEY = 'test_campaign_call_timestamp'

const WAITING_AFTER_TEST_CAMPAIGN = 15000
const DEBOUNCE_TIME = 500

type CampaignSetupProps = {
	openSidebar: () => void
}

export default function CampaignSetup({ openSidebar }: CampaignSetupProps) {
	const { FRONTEND_MAINTENANCE_MODE, FRONTEND_HARDCODED_AGENT_IDS } = useFeatureFlags()
	const { PROPAI_HELP_EMAIL } = useCommonVars()
	const dispatch = useAppDispatch()
	const campaign = useAppSelector(selectDetailsData) as Campaign

	const [phoneNumber, setPhoneNumber] = useState('')
	const [advanceSettings, setAdvanceSettings] = useState({
		[AdvanceSettings.CALL_UNCALLED_ONLY]: campaign?.campaign_state.code === 'error',
		[AdvanceSettings.AUTORUN_AFTER_COMPLETED]: false,
		[AdvanceSettings.SKIP_ANSWERED]: false,
		[AdvanceSettings.FINISH_IF_NO_CREDITS]: false,
		[AdvanceSettings.FINISH_RUN_OUT_CREDITS]: false,
		[AdvanceSettings.AUTORUN_AFTER_COMPLETED_HOURS]: 2,
		[AdvanceSettings.FINISH_IF_NO_CREDITS_TIME]: '08:00',
	})
	const [defaultAdvanceSettings] = useState(advanceSettings)

	const [waitingForTestCall, setWaitingForTestCall] = useState(false)

	const { campaignTypes } = useCampaignTypes()
	const [selectedAgentId, setSelectedAgentId] = useState<string>(campaign?.agent_id || '')
	const { agent: selectedAgentData } = useFetchAgent(selectedAgentId)
	const [openingHooks, setOpeningHooks] = useState<string[]>([])
	const [customOpeningHook, setCustomOpeningHook] = useState<string>(campaign.custom_opening_hook || '')
	const [typingOpeningHookInProgress, setTypingOpeningHookInProgress] = useState(false)
	const [receiverLabel, setReceiverLabel] = useState<string>(t('run_campaign.company_name_label'))
	const [companyNameHelperText, setCompanyNameHelperText] = useState<string>()

	const [formData, setFormData] = useState(() => {
		return {
			agentName: campaign.agent_name,
			followUpCallerName: campaign.follow_up_caller_name,
			companyName: campaign.company_name,
			campaignType: campaign.campaign_type,
			firstSentence: campaign.first_sentence,
			adviceForAi: campaign?.prompt_injection,
			agentId: campaign.agent_id,
			openingHook: campaign.opening_hook || (openingHooks.length > 0 ? openingHooks[0] : ''),
			customOpeningHook: customOpeningHook,
		}
	})

	useEffect(() => {
		setFormData((prevState) => ({
			...prevState,
			campaignType: campaign.campaign_type,
			firstSentence: campaign.first_sentence,
			agentId: campaign.agent_id,
		}))
	}, [campaign])

	const isValidPhoneNumber = useCallback((number: any) => validatePhoneNumber(number), [validatePhoneNumber])

	const updateCampaignOnChange = useCallback(
		debounce((e) => {
			console.log('updating campaign...')
			dispatch(
				actionUpdateCampaignDetails({
					campaignId: campaign.campaign_id,
					updates: { [e.target.name]: e.target.value },
				}),
			)
		}, DEBOUNCE_TIME),
		[campaign],
	)

	const handleTestNumberChange = (e: any) => {
		setPhoneNumber(e.target.value)
		if (isValidPhoneNumber(e.target.value)) {
			updateCampaignOnChange(e)
		}
	}

	useInterval(() => {
		if (waitingForTestCall) {
			const remainingTime = getTimeRemaining(
				String(localStorage.getItem(LOCAL_STORAGE_KEY as string)),
				WAITING_AFTER_TEST_CAMPAIGN,
			)
			setWaitingForTestCall(!!remainingTime)
		}
	}, 1000)

	const updateCampaignType = (type: any) => {
		if (type.isAgent) {
			setSelectedAgentId(type.value)
			dispatch(
				actionUpdateCampaignDetails({
					campaignId: campaign.campaign_id,
					updates: {
						campaignId: campaign.campaign_id,
						agentId: type.value,
						firstSentence: type.introSentence,
					},
				}),
			)
		} else {
			dispatch(
				actionUpdateCampaignDetails({
					campaignId: campaign.campaign_id,
					updates: {
						campaignId: campaign.campaign_id,
						campaignType: type.value,
					},
				}),
			)
		}
	}

	const handlePropChange = (propName: AdvanceSettings, event: any) => {
		setAdvanceSettings({ ...advanceSettings, [propName]: event.target.checked })
	}

	const handleTestCampaign = async () => {
		try {
			setWaitingForTestCall(true)
			localStorage.setItem(LOCAL_STORAGE_KEY, new Date() as any)
			// if campaign.agent_id is not none, then we need to pass it to the runCampaign function
			console.log('campaign.agent_id', campaign.agent_id)
			if (campaign.agent_id) {
				await runCampaign({
					campaignId: campaign?.campaign_id,
					testPhoneNumber: phoneNumber,
					agentId: campaign.agent_id,
				} as any)
			} else {
				await runCampaign({ campaignId: campaign?.campaign_id, testPhoneNumber: phoneNumber } as any)
			}
		} catch (e) {
			setWaitingForTestCall(false)
		}
	}

	const handleSelectOpeningHook = (event: any) => {
		const selectedHook = event.target.value
		console.log('selectedHook', selectedHook)
		setTypingOpeningHookInProgress(false)
		setFormData({ ...formData, openingHook: selectedHook })
		updateCampaignOnChange({
			target: { name: 'openingHook', value: selectedHook },
		})
	}
	// if campaign changes set customOpeningHook to campaign.custom_opening_hook
	useEffect(() => {
		if (campaign.custom_opening_hook) {
			setCustomOpeningHook(campaign.custom_opening_hook)
		}
	}, [campaign])

	const handleSelectOpeningHookCustom = (event: any) => {
		const customValue = event.target.value
		setTypingOpeningHookInProgress(true)
		setCustomOpeningHook(customValue)
		setFormData({ ...formData, openingHook: customValue, customOpeningHook: customValue })

		setTimeout(() => {
			setTypingOpeningHookInProgress(false)
		}, 1000)

		if (customValue.trim() !== '') {
			updateCampaignOnChange({
				target: { name: 'openingHook', value: customValue, customOpeningHook: customValue },
			})
		} else {
			updateCampaignOnChange({
				target: { name: 'openingHook', value: null, customOpeningHook: null },
			})
		}
	}

	const handleFormChange = (event: any) => {
		setFormData({ ...formData, [event.target.name]: event.target.value })
	}

	const selectedType = useMemo(
		() => (formData.agentId ? formData.agentId : formData.campaignType || campaignTypes[0].value),
		[formData, campaignTypes],
	)

	useEffect(() => {
		if (campaign?.agent_id && !selectedAgentId) {
			setSelectedAgentId(campaign.agent_id)
		}
	}, [campaign, selectedAgentId])

	useEffect(() => {
		if (selectedAgentData) {
			setOpeningHooks(
				Array.isArray(selectedAgentData?.agent_opening_hook) ? selectedAgentData.agent_opening_hook : [],
			)
		}
	}, [selectedAgentData])

	useEffect(() => {
		const agent: any = Object.values(FRONTEND_HARDCODED_AGENT_IDS).find(
			(agent: any) => agent.id === formData.agentId,
		)
		if (agent?.receiver) {
			setReceiverLabel(agent.receiver.label)
			setCompanyNameHelperText(agent.receiver.value)
		} else {
			setReceiverLabel(t('run_campaign.company_name_label'))
			setCompanyNameHelperText('ABC capital')
		}
	}, [FRONTEND_HARDCODED_AGENT_IDS, formData.agentId])

	useEffect(() => {
		console.log('update saved campaign...')
		dispatch(
			actionSetCampaignPayload({
				campaignId: campaign.campaign_id,
				retryCampaign: advanceSettings[AdvanceSettings.CALL_UNCALLED_ONLY],
				agentId: campaign.agent_id,
			}),
		)
	}, [dispatch, campaign, advanceSettings])

	return (
		<Grid container spacing={4} direction="row" sx={{ px: '16px' }}>
			<Grid item xs={12} sx={{ paddingLeft: '20px!important' }}>
				<Typography variant="h3" color="black" mb="20px">
					{t('run_campaign.setup_title')}
				</Typography>
				<InputLabel shrink={false} htmlFor={'campaignType'} required sx={inputLabelContainerStyle}>
					<Typography sx={inputLabelStyle} variant="h5" component="span">
						{t('run_campaign.select_script_type_label')}
					</Typography>
				</InputLabel>
				<CampaignType
					campaignTypes={campaignTypes}
					selectedType={selectedType}
					updateCampaignType={updateCampaignType}
					name="campaignType"
					selectProps={{ sx: { fontSize: '12px' } }}
					itemProps={{ sx: { fontSize: '12px' } }}
				/>
				<Box sx={learnMoreStyle} onClick={openSidebar}>
					<Info style={{ width: '18px', height: '18px' }} />
					<Typography variant="h3" color="black">
						{t('run_campaign.help_link')}
					</Typography>
				</Box>
			</Grid>
			<Grid item xs={4} sx={{ paddingTop: '8px!important', paddingLeft: '20px!important' }}>
				<Box sx={labelTooltipContainerStyle}>
					<InputLabel shrink={false} htmlFor={'agentName'} required sx={inputLabelContainerStyle}>
						<Typography sx={inputLabelStyle} variant="h5" component="span">
							{t('run_campaign.agent_name_label')}
						</Typography>
					</InputLabel>
					<Tooltip
						arrow
						placement="top"
						title={t('run_campaign.agent_name_label_i')}
						sx={{ display: 'inline' }}
					>
						<Info style={{ width: '18px', height: '18px' }} />
					</Tooltip>
				</Box>
				<TextField
					name="agentName"
					value={formData.agentName}
					variant="outlined"
					InputProps={{ style: { fontSize: '12px' } }}
					onChange={handleFormChange}
					onBlur={updateCampaignOnChange}
					autoComplete="off"
					fullWidth
				/>
			</Grid>
			<Grid item xs={4} sx={{ paddingTop: '8px!important', paddingLeft: '20px!important' }}>
				<Box sx={labelTooltipContainerStyle}>
					<InputLabel shrink={false} htmlFor={'followUpCallerName'} required sx={inputLabelContainerStyle}>
						<Typography sx={inputLabelStyle} variant="h5" component="span">
							{t('run_campaign.follow_up_caller_label')}
						</Typography>
					</InputLabel>
					<Tooltip
						arrow
						placement="top"
						title={t('run_campaign.follow_up_caller_label_i')}
						sx={{ display: 'inline' }}
					>
						<Info style={{ width: '18px', height: '18px' }} />
					</Tooltip>
				</Box>
				<TextField
					name="followUpCallerName"
					value={formData.followUpCallerName}
					variant="outlined"
					InputProps={{ style: { fontSize: '12px' } }}
					onChange={handleFormChange}
					onBlur={updateCampaignOnChange}
					autoComplete="off"
					fullWidth
				/>
			</Grid>
			<Grid item xs={4} sx={{ paddingTop: '8px!important', paddingLeft: '20px!important' }}>
				<Box sx={labelTooltipContainerStyle}>
					<InputLabel shrink={false} htmlFor={'companyName'} required sx={inputLabelContainerStyle}>
						<Typography sx={inputLabelStyle} variant="h5" component="span">
							{receiverLabel}
						</Typography>
					</InputLabel>
					<Tooltip
						arrow
						placement="top"
						title={t('run_campaign.company_name_label_i')}
						sx={{ display: 'inline' }}
					>
						<Info style={{ width: '18px', height: '18px' }} />
					</Tooltip>
				</Box>
				<TextField
					name="companyName"
					helperText={`Ex: ${companyNameHelperText}`}
					value={formData.companyName}
					variant="outlined"
					InputProps={{ style: { fontSize: '12px' } }}
					onChange={handleFormChange}
					onBlur={updateCampaignOnChange}
					autoComplete="off"
					fullWidth
					InputLabelProps={{
						shrink: true,
					}}
				/>
			</Grid>

			<Grid item xs={12} sx={{ paddingTop: '16px!important', paddingLeft: '20px!important' }}>
				<Accordion
					sx={{
						boxShadow: 'none',
						'&:before': { display: 'none' }, // Removes the default divider
						marginTop: '16px',
						width: '100%',
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon sx={{ fontSize: '20px', color: 'text.secondary' }} />}
						aria-controls="modify-ai-behavior-content"
						id="modify-ai-behavior-header"
						sx={{
							padding: 0,
							minHeight: '40px',
							'& .MuiAccordionSummary-content': {
								margin: 0,
							},
						}}
					>
						<Typography variant="h3" color="black" mb="20px">
							{t('run_campaign.modify_ai_behavior_title')}
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ padding: '8px 0 0' }}>
						<Grid item xs={12} sx={{ paddingTop: '16px!important' }}>
							<Box sx={labelTooltipContainerStyle}>
								<InputLabel
									shrink={false}
									htmlFor={'firstSentence'}
									sx={inputLabelContainerStyle}
									required
								>
									<Typography sx={inputLabelStyle} variant="h5" component="span">
										<Trans i18nKey="run_campaign.intro_sentence_label" />
									</Typography>
								</InputLabel>
								<Tooltip
									arrow
									placement="top"
									title={t('run_campaign.intro_sentence_label_i')}
									sx={{ display: 'inline' }}
								>
									<Info style={{ width: '18px', height: '18px' }} />
								</Tooltip>
							</Box>
							<TextField
								name="firstSentence"
								value={formData.firstSentence}
								variant="outlined"
								InputProps={{ style: { fontSize: '12px' } }}
								multiline
								onChange={handleFormChange}
								onBlur={updateCampaignOnChange}
								autoComplete="off"
								fullWidth
							/>
						</Grid>
						{openingHooks.length > 0 && (
							<OpeningHookCustomSelector
								openingHooks={openingHooks}
								value={formData.openingHook}
								customValue={formData.customOpeningHook}
								typingOpeningHookInProgress={typingOpeningHookInProgress}
								onChange={handleSelectOpeningHook}
								setCustomOpeningHook={handleSelectOpeningHookCustom}
							/>
						)}
						<Grid item xs={12} sx={{ paddingTop: '16px!important' }}>
							<Box sx={labelTooltipContainerStyle}>
								<InputLabel shrink={false} htmlFor={'adviceForAi'} sx={inputLabelContainerStyle}>
									<Typography sx={inputLabelStyle} variant="h5" component="span">
										<Trans
											i18nKey="run_campaign.advice_for_ai_label"
											components={{
												opt: (
													<Typography
														sx={inputLabelStyle}
														variant="h5"
														component="span"
														className="optional"
													/>
												),
											}}
										/>
									</Typography>
								</InputLabel>
								<Tooltip
									arrow
									placement="top"
									title={t('run_campaign.advice_for_ai_label_i')}
									sx={{ display: 'inline' }}
								>
									<Info style={{ width: '18px', height: '18px' }} />
								</Tooltip>
							</Box>
							<TextField
								name="adviceForAi"
								value={formData.adviceForAi}
								placeholder="Make sure you ask them 3 times for their lowest price."
								variant="outlined"
								multiline
								InputProps={{ style: { fontSize: '12px' } }}
								onChange={handleFormChange}
								onBlur={updateCampaignOnChange}
								autoComplete="off"
								fullWidth
							/>
						</Grid>
					</AccordionDetails>
				</Accordion>
			</Grid>
			<Grid item xs={12} sx={{ paddingLeft: '20px!important' }}>
				<Typography variant="h3" color="black">
					{t('run_campaign.test_call_title')}
				</Typography>
			</Grid>
			<Grid item xs={8} sx={{ paddingTop: '16px!important', paddingLeft: '20px!important' }}>
				<Box sx={labelTooltipContainerStyle}>
					<InputLabel shrink={false} htmlFor={'testPhoneNumber'} sx={inputLabelContainerStyle}>
						<Typography sx={inputLabelStyle} variant="h5" component="span">
							<Trans
								i18nKey="run_campaign.test_phone_number_label"
								components={{
									opt: (
										<Typography
											sx={inputLabelStyle}
											variant="h5"
											component="span"
											className="optional"
										/>
									),
								}}
							/>
						</Typography>
					</InputLabel>
					<Tooltip arrow placement="top" title={<Trans i18nKey="run_campaign.test_phone_number_label_i" />}>
						<Info style={{ width: '18px', height: '18px' }} />
					</Tooltip>
				</Box>
				<TextField
					name="testPhoneNumber"
					placeholder="Enter a valid phone number"
					variant="outlined"
					fullWidth
					onChange={handleTestNumberChange}
					InputProps={{
						inputComponent: PhoneNumberMask as any,
						style: { fontSize: '12px' },
					}}
				/>
			</Grid>
			<Grid
				item
				xs={4}
				sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', paddingLeft: '20px!important' }}
			>
				<Button
					fullWidth
					variant="contained"
					onClick={handleTestCampaign}
					disabled={FRONTEND_MAINTENANCE_MODE || !isValidPhoneNumber(phoneNumber) || waitingForTestCall}
					sx={testButtonStyle}
				>
					{waitingForTestCall ? <CircularProgress /> : t('run_campaign.modal_test_button')}
				</Button>
			</Grid>
			<Grid item xs={12} sx={{ paddingTop: '16px!important', paddingLeft: '20px!important' }}>
				<Typography variant="body1" sx={modalSubtitleStyle}>
					<Trans
						i18nKey="run_campaign.modal_test_advice"
						components={{
							anchor: <a href={`mailto:${PROPAI_HELP_EMAIL}`} style={linkStyle} />,
						}}
					/>
				</Typography>
			</Grid>
			<Grid
				item
				xs={12}
				sx={{
					paddingTop: '56px!important',
					display: 'flex',
					justifyContent: 'space-between',
					paddingLeft: '20px!important',
				}}
			>
				<Typography variant="h3" color="black">
					{t('run_campaign.advance_settings_title')}
				</Typography>
				<Box sx={refreshStyle} onClick={() => setAdvanceSettings(defaultAdvanceSettings)}>
					<RefreshCw style={{ width: '20px', height: '20px' }} />
					<Typography variant="h3" color="black">
						{t('run_campaign.reset_default_label')}
					</Typography>
				</Box>
			</Grid>
			<SettingsList
				advanceSettings={advanceSettings}
				setAdvanceSettings={setAdvanceSettings}
				handlePropChange={handlePropChange}
			/>
		</Grid>
	)
}
